import { jsx as _jsx } from "react/jsx-runtime";
import { ErrorBoundary as DataDogErrorBoundry } from '@datadog/browser-rum-react';
import { APP_CRASH_KEY } from 'constant';
import { logger } from 'services';
import { captureChunkErrorTelemetry, isChunkLoadError, markChunkErrorReloadAttempt, reloadAfterChunkError, shouldLogTerminalChunkError, wasRecentlyReloadedForChunkError } from 'utils/chunk-error';
// DD's boundary re-invokes the fallback on every render pass while in error
// state, so unguarded logging counts fallback renders, not failures. Chunk
// paths dedup via shouldLogTerminalChunkError; this guards the non-chunk
// contained path once per (boundary, message) per page load.
const containedErrorsLogged = new Set();
const defaultOnError = (error, boundaryName, impact = 'app')=>{
    var _ref;
    const name = boundaryName !== null && boundaryName !== void 0 ? boundaryName : 'unnamed';
    if (isChunkLoadError(error)) {
        var _telemetry_chunkUrl;
        if (!wasRecentlyReloadedForChunkError()) {
            markChunkErrorReloadAttempt();
            reloadAfterChunkError(undefined, {
                name: error === null || error === void 0 ? void 0 : error.name,
                message: error === null || error === void 0 ? void 0 : error.message
            });
            return;
        }
        // Terminal: reload already spent within the debounce window. Log once
        // per (session, chunkUrl) with the diagnostic telemetry — previously
        // this path was a silent return, invisible in DD.
        const telemetry = captureChunkErrorTelemetry(error);
        // Dedup scope is namespaced by impact so the two signatures can never
        // suppress each other when both boundary kinds hit the same chunk URL.
        if (shouldLogTerminalChunkError(String((_telemetry_chunkUrl = telemetry.chunkUrl) !== null && _telemetry_chunkUrl !== void 0 ? _telemetry_chunkUrl : ''), impact === 'contained' ? 'contained-boundary' : 'nested-boundary')) {
            if (impact === 'contained') {
                logger.warn(`__contained_chunk_failure: ${name}`, {
                    error,
                    ...telemetry
                });
            } else {
                logger.error(`${APP_CRASH_KEY}: ChunkLoadError - already reloaded`, {
                    error,
                    ...telemetry
                });
            }
        }
        return;
    }
    if (impact === 'contained') {
        var _ref1;
        // Structural key — delimiter concatenation would collide when a name or
        // message itself contains the separator.
        const dedupKey = JSON.stringify([
            name,
            (_ref1 = error === null || error === void 0 ? void 0 : error.message) !== null && _ref1 !== void 0 ? _ref1 : ''
        ]);
        if (!containedErrorsLogged.has(dedupKey)) {
            var _ref2;
            containedErrorsLogged.add(dedupKey);
            logger.warn(`__contained_component_error: ${name} - ${(_ref2 = error === null || error === void 0 ? void 0 : error.message) !== null && _ref2 !== void 0 ? _ref2 : 'Unknown'}`, {
                error,
                errorStack: error === null || error === void 0 ? void 0 : error.stack,
                errorName: error === null || error === void 0 ? void 0 : error.name
            });
        }
        return;
    }
    logger.error(`${APP_CRASH_KEY}: Component error - ${(_ref = error === null || error === void 0 ? void 0 : error.message) !== null && _ref !== void 0 ? _ref : 'Unknown'}`, {
        error,
        errorStack: error === null || error === void 0 ? void 0 : error.stack,
        errorName: error === null || error === void 0 ? void 0 : error.name
    });
};
export const ErrorBoundary = ({ fallback, onError, boundaryName, impact, children })=>{
    const _fallback = ({ error })=>{
        var _ref;
        if (onError) {
            onError();
        } else {
            defaultOnError(error, boundaryName, impact);
        }
        return (_ref = fallback === null || fallback === void 0 ? void 0 : fallback(error)) !== null && _ref !== void 0 ? _ref : null;
    };
    return /*#__PURE__*/ _jsx(DataDogErrorBoundry, {
        fallback: _fallback,
        children: children
    });
};
